import axios from 'axios';
import API_CONFIG from '../config/api';

// Get auth headers with token
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
};

// Interface for calendar data
export interface CalendarData {
  _id: string;
  date: string; // YYYY-MM-DD format
  startTime: string; // HH:MM format
  endTime: string; // HH:MM format
  capacity: number;
  isVacation: boolean;
  type: 'home' | 'lab';
  createdAt: string;
  updatedAt: string;
}

// Interface for appointment booking data
export interface AppointmentBooking {
  requestId: string;
  date: string;
  time: string;
  type: 'home' | 'lab';
}

// Default calendar settings for normal working days
// These are used when no exception exists in the database
// You can modify these values as needed for your business requirements
const DEFAULT_CALENDAR_SETTINGS = {
  startTime: '08:00',  // Default start time 8:00 AM
  endTime: '18:00',    // Default end time 6:00 PM  
  capacity: 30,        // Default maximum appointments per day (will be overridden by settings)
  isVacation: false    // Normal working day (not vacation)
};

/**
 * Get calendar data for a specific date
 * Returns null if no exception exists in database (meaning use defaults)
 */
export const getCalendarByDate = async (date: string, type: 'home' | 'lab'): Promise<CalendarData | null> => {
  try {

    const response = await axios.get(
      API_CONFIG.getUrl(`calendar/date/${date}`),
      {
        params: { type },
        ...getAuthHeaders(),
      }
    );

    // If calendar exception exists in database, return it
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    
    // If no calendar exception exists, return null (use defaults)
    return null;
  } catch (error: any) {
    // If 404 or no data found, it means no exception - return null
    if (error.response?.status === 404) {

      return null;
    }

    return null;
  }
};

/**
 * Get capacity usage for a specific date
 */
export const getCapacityUsage = async (date: string, type: 'home' | 'lab'): Promise<number> => {
  try {
    const response = await axios.get(
      API_CONFIG.getUrl(`calendar/${date}/capacity`),
      {
        params: { type },
        ...getAuthHeaders(),
      }
    );
    
    return response.data.success ? response.data.data.usedCapacity : 0;
  } catch (error: any) {

    return 0;
  }
};

/**
 * Book an appointment
 */
export const bookAppointment = async (bookingData: AppointmentBooking) => {
  try {
    const response = await axios.post(
      API_CONFIG.getUrl('calendar/book'),
      bookingData,
      getAuthHeaders()
    );
    
    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data || null
    };
  } catch (error: any) {

    // Handle validation errors from backend
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    
    throw error;
  }
};

/**
 * Generate time slots based on calendar data (or defaults if none exists)
 */
export const generateTimeSlots = (
  calendarData: CalendarData | null,
  isCapacityFull: boolean = false
): Array<{time: string, persianTime: string, available: boolean, isBooked: boolean}> => {
  // Use calendar data if available, otherwise use defaults
  const startTime = calendarData?.startTime || DEFAULT_CALENDAR_SETTINGS.startTime;
  const endTime = calendarData?.endTime || DEFAULT_CALENDAR_SETTINGS.endTime;
  const isVacation = calendarData?.isVacation || DEFAULT_CALENDAR_SETTINGS.isVacation;
  
  // If it's a vacation day, return empty slots
  if (isVacation) {
    return [];
  }
  
  const slots = [];
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;
  
  // Generate 30-minute slots
  for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    
    const persianTime = new Intl.DateTimeFormat('fa-IR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(`2000-01-01T${time}:00`));
    
    slots.push({ 
      time, 
      persianTime,
      available: !isCapacityFull,
      isBooked: false
    });
  }
  
  return slots;
};

/**
 * Convert Persian (Shamsi) date to Gregorian date
 * This is a simplified implementation - you may need to use a proper library like moment-jalaali
 */
export const convertPersianToGregorian = (persianYear: number, persianMonth: number, persianDay: number): string => {
  // This is a placeholder implementation
  // For a proper implementation, you should use a library like moment-jalaali
  // For now, we'll create a simple approximation
  
  // Convert Persian date to Gregorian
  // This is a very simplified conversion - use a proper library in production
  const baseYear = 1979; // Approximate offset between Persian and Gregorian calendars
  const gregorianYear = persianYear + baseYear;
  
  // Create a date object (this is simplified)
  const date = new Date(gregorianYear, persianMonth - 1, persianDay);
  
  // Return in YYYY-MM-DD format
  return date.toISOString().split('T')[0];
};

/**
 * Get today's date in Persian calendar and convert to Gregorian
 */
export const getTodayInPersian = (): string => {
  const today = new Date();
  
  // Get Persian date using Intl.DateTimeFormat
  const persianDate = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(today);
  
  // Parse the Persian date
  const [year, month, day] = persianDate.split('/').map(Number);
  
  // Convert to Gregorian
  return convertPersianToGregorian(year, month, day);
};

/**
 * Get default capacity from settings
 */
export const getDefaultCapacity = async (): Promise<number> => {
  try {
    const response = await axios.get(`${API_CONFIG.baseURL}/settings`);
    if (response.data.success && response.data.data.defaultCapacity) {
      return response.data.data.defaultCapacity;
    }
    return DEFAULT_CALENDAR_SETTINGS.capacity;
  } catch (error) {

    return DEFAULT_CALENDAR_SETTINGS.capacity;
  }
};

/**
 * Get booked time slots for a specific date and type
 */
export const getBookedTimeSlots = async (date: string, type: 'home' | 'lab'): Promise<string[]> => {
  try {

    const response = await axios.get(`${API_CONFIG.baseURL}/calendar/${date}/${type}/booked-slots`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}` // Fixed: use correct token key
      }
    });

    if (response.data.success) {
      const bookedSlots = response.data.data.bookedTimeSlots || [];

      return bookedSlots;
    }
    return [];
  } catch (error: any) {

    return [];
  }
};

/**
 * Check if capacity is full for a specific date
 */
export const isCapacityFull = async (date: string, type: 'home' | 'lab'): Promise<boolean> => {
  try {
    const calendarData = await getCalendarByDate(date, type);
    const usedCapacity = await getCapacityUsage(date, type);
    
    // Get capacity from calendar data or use default from settings
    let totalCapacity;
    if (calendarData?.capacity !== undefined) {
      totalCapacity = calendarData.capacity;
    } else {
      // Use default capacity from settings
      totalCapacity = await getDefaultCapacity();
    }

    // If capacity is 0, it means no appointments allowed (always unavailable)
    if (totalCapacity === 0) {

      return true;
    }
    
    // Capacity is full when used capacity reaches or exceeds total capacity
    const isFull = usedCapacity >= totalCapacity;
    
    if (isFull) {

    } else {

    }
    
    return isFull;
  } catch (error: any) {

    return false;
  }
}; 